import { useLazyQuery, useMutation } from '@apollo/client';
import {
	AutocompleteFilterDefinitionType,
	Chip,
	FilterMenuOutputType,
	Icon,
	IconButton,
	SelectFilterDefinitionType,
	Table,
	TableColumnType,
	TableInstance,
	TableOrderByType,
	Tooltip,
} from '@elipssolution/harfang';
import { mdiClose, mdiDelete, mdiEmailArrowRight } from '@mdi/js';
import { Stack } from '@mui/material';
import { useCallback, useMemo, MouseEvent, useState } from 'react';

import { useConfirmationDialogContext } from '../../../../../components/ConfirmationDialogProvider';
import { useSession } from '../../../../../src/components/session/SessionContext';
import { PermissionEnum } from '../../../../../types/permission';
import { SUCCESS_ANIMATION_DELAY } from '../../../../../utils/statusAnimationDelay';
import {
	FETCH_ONGOING_AND_DRAFT_PROCEDURES,
	FetchOngoingAndDraftProceduresType,
	REMIND_PROCEDURE_AWAITED_SIGNERS,
	RemindProcedureAwaitedSignersType,
	CANCEL_PROCEDURE,
	CancelProcedureType,
	REMOVE_PROCEDURE,
	RemoveProcedureType,
} from '../../../api/procedure';
import {
	FETCH_SIGNERS_IN_ONGOING_OR_DRAFT_PROCEDURES,
	FetchSignersInOngoingOrDraftProceduresType,
} from '../../../api/signer';
import ProcedureConsultationDialog from '../../../components/ProcedureConsultationDialog';
import { ProcedureStatusEnum, ProcedureType } from '../../../types/procedure';
import { SignerType } from '../../../types/signer';
import { baseProcedureColumns } from '../../../utils/procedureBaseColumns';
import { RenderSigner, renderSignerFullName } from '../../../utils/renderSigner';

type ProcedureFilterType = {
	isPrivate: SelectFilterDefinitionType<boolean>;
	signer: AutocompleteFilterDefinitionType<SignerType>;
	status: SelectFilterDefinitionType<ProcedureStatusEnum>;
};

const baseColumns: TableColumnType<ProcedureType>[] = [
	...baseProcedureColumns.slice(0, 2),
	{
		field: 'status',
		key: 'status',
		sortable: true,
		title: 'Statut',
		render: ({ status }) => (
			<Chip
				label={status === ProcedureStatusEnum.ONGOING ? 'En cours' : 'Brouillon'}
				color={status === ProcedureStatusEnum.ONGOING ? 'info' : 'default'}
			/>
		),
		width: 85,
		flexGrow: 0,
	},
	...baseProcedureColumns.slice(2),
];
type OngoingProceduresTableProps = {
	onDraftProcedureClick: (procedureId: ProcedureType['id']) => void;
	tableInstance: TableInstance;
};

const OngoingProceduresTable = ({ onDraftProcedureClick, tableInstance }: OngoingProceduresTableProps) => {
	const { checkPermission } = useSession();
	const { confirmAction } = useConfirmationDialogContext();

	const [procedureConsultationId, setProcedureConsultationId] = useState<ProcedureType['id']>();

	const [fetchOngoingAndDraftProcedures] = useLazyQuery<FetchOngoingAndDraftProceduresType>(
		FETCH_ONGOING_AND_DRAFT_PROCEDURES,
	);

	const proceduresDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
			orderBy?: TableOrderByType<ProcedureType>,
			filters?: FilterMenuOutputType<ProcedureFilterType>,
		): Promise<{
			count: number;
			items: ProcedureType[];
		}> => {
			const { field, order } = orderBy || {};
			const { signer: signerFilter, ...restFilters } = filters ?? {};

			const { data, error } = await fetchOngoingAndDraftProcedures({
				variables: {
					...(!!filters && {
						filter: {
							...restFilters,
							...(signerFilter && {
								signerFirstName: { eq: signerFilter.eq?.firstName },
								signerLastName: { eq: signerFilter.eq?.lastName },
							}),
						},
					}),
					...(orderBy && { orderBy: { field, order } }),
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				sign_ongoingAndDraftProcedures: { count = 0, items = [] },
			} = data ?? {
				sign_ongoingAndDraftProcedures: {},
			};

			return {
				count,
				items: items.map(({ updatedAt, ...rest }) => ({
					...rest,
					updatedAt: new Date(updatedAt),
				})),
			};
		},
		[fetchOngoingAndDraftProcedures],
	);

	const [fetchSignersInOngoingOrDraftProcedures] = useLazyQuery<FetchSignersInOngoingOrDraftProceduresType>(
		FETCH_SIGNERS_IN_ONGOING_OR_DRAFT_PROCEDURES,
	);

	const signersInOngoingOrDraftProceduresDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			count: number;
			items: SignerType[];
		}> => {
			const { data, error } = await fetchSignersInOngoingOrDraftProcedures({
				variables: {
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				sign_signersInOngoingOrDraftProcedures: { count = 0, items = [] },
			} = data ?? {
				sign_signersInOngoingOrDraftProcedures: {},
			};

			return { count, items };
		},
		[fetchSignersInOngoingOrDraftProcedures],
	);

	const [remindProcedureAwaitedSigners] = useMutation<RemindProcedureAwaitedSignersType>(
		REMIND_PROCEDURE_AWAITED_SIGNERS,
	);

	const handleRemindProcedureAwaitedSigners = useCallback(
		async ({ event, id }: { event: MouseEvent<HTMLButtonElement>; id: ProcedureType['id'] }) => {
			event.stopPropagation();

			await remindProcedureAwaitedSigners({
				variables: {
					signRemindProcedureAwaitedSignersId: id,
				},
			});
		},
		[remindProcedureAwaitedSigners],
	);

	const [cancelProcedure] = useMutation<CancelProcedureType>(CANCEL_PROCEDURE, {
		onCompleted: () => setTimeout(() => tableInstance.reload(), SUCCESS_ANIMATION_DELAY),
	});

	const handleCancelProcedure = useCallback(
		async ({ event, id }: { event: MouseEvent<HTMLButtonElement>; id: ProcedureType['id'] }) => {
			event.stopPropagation();

			await cancelProcedure({
				variables: {
					signCancelProcedureId: id,
				},
			});
		},

		[cancelProcedure],
	);

	const [removeProcedure] = useMutation<RemoveProcedureType>(REMOVE_PROCEDURE);

	const handleRemoveButtonClick = useCallback(
		async (
			event: MouseEvent<HTMLButtonElement>,
			procedureId: ProcedureType['id'],
			procedureName: ProcedureType['name'],
		) => {
			event.stopPropagation();

			const isRemovalConfirmed = await confirmAction({
				title: `Êtes-vous sûr de vouloir supprimer la procédure ${procedureName} ?`,
				confirmButtonDetails: {
					color: 'error',
					label: 'Supprimer',
				},
			});

			if (!isRemovalConfirmed) return null;

			const result = await removeProcedure({
				variables: {
					signRemoveProcedureId: procedureId,
				},
			});

			tableInstance.reload();

			return result;
		},
		[confirmAction, removeProcedure, tableInstance],
	);

	const columns: TableColumnType<ProcedureType>[] = useMemo(
		() => [
			...baseColumns,
			{
				align: 'right',
				key: 'actions',
				flexGrow: 0,
				render: ({ id, name, status }) =>
					status === ProcedureStatusEnum.ONGOING ? (
						<Stack flexDirection="row-reverse">
							<IconButton
								onClick={(event) => handleCancelProcedure({ event, id })}
								tooltipContent="Annuler la procédure"
							>
								<Icon path={mdiClose} />
							</IconButton>
							<IconButton
								onClick={(event) => handleRemindProcedureAwaitedSigners({ event, id })}
								tooltipContent="Relancer les signataires"
							>
								<Icon path={mdiEmailArrowRight} />
							</IconButton>
						</Stack>
					) : (
						<Tooltip placement="left" content="Supprimer la procédure">
							<IconButton onClick={(event) => handleRemoveButtonClick(event, id, name)}>
								<Icon path={mdiDelete} />
							</IconButton>
						</Tooltip>
					),
				width: 80,
			},
		],
		[handleCancelProcedure, handleRemindProcedureAwaitedSigners, handleRemoveButtonClick],
	);

	const filters: ProcedureFilterType = useMemo(
		() => ({
			isPrivate: {
				label: 'Visibilité',
				type: 'select',
				options: [true, false],
				renderOption: (value) => (value ? 'Privée' : 'Publique'),
				renderValue: (value) => (value ? 'Privée' : 'Publique'),
			},
			signer: {
				key: 'email',
				label: 'Signataire',
				type: 'autocomplete',
				dataSource: signersInOngoingOrDraftProceduresDataSource,
				getOptionLabel: renderSignerFullName,
				renderOptions: RenderSigner,
			},
			status: {
				label: 'Statut',
				type: 'select',
				options: [ProcedureStatusEnum.ONGOING, ProcedureStatusEnum.DRAFT],
				renderOption: (value) => (value === ProcedureStatusEnum.ONGOING ? 'En cours' : 'Brouillon'),
				renderValue: (value) => (value === ProcedureStatusEnum.ONGOING ? 'En cours' : 'Brouillon'),
			},
		}),
		[signersInOngoingOrDraftProceduresDataSource],
	);

	const handleRowClick = useCallback(
		({ id, status }: ProcedureType) => {
			if (status === ProcedureStatusEnum.ONGOING || !checkPermission(PermissionEnum.SIGN_PROCEDURE_WRITE)) {
				setProcedureConsultationId(id);
			} else {
				onDraftProcedureClick(id);
			}
		},
		[checkPermission, onDraftProcedureClick],
	);

	const handleProcedureViewDialogClose = useCallback(() => setProcedureConsultationId(undefined), []);

	return (
		<Stack height="100%" width="100%" flex={3}>
			<Table<ProcedureType, ProcedureFilterType>
				columns={checkPermission(PermissionEnum.SIGN_PROCEDURE_WRITE) ? columns : baseColumns}
				dataSource={proceduresDataSource}
				filters={filters}
				onRowClick={handleRowClick}
				table={tableInstance}
				title="Procédures en cours"
				enableSearch
			/>

			<ProcedureConsultationDialog
				procedureId={procedureConsultationId}
				tableInstance={tableInstance}
				onClose={handleProcedureViewDialogClose}
			/>
		</Stack>
	);
};

export default OngoingProceduresTable;
